import { useParams, usePathname } from 'next/navigation';

import { PopoverButton } from '@headlessui/react';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { getIsHeaderDatepickerGlobalDisabled } from '@/utils/layout/getIsHeaderDatepickerGlobalDisabled';

import { periodsTranslation } from '@/lib/periods';

import { ButtonStyle } from '@/components/base/Button';
import type { DatepickerRangeAtomImmer } from '@/components/base/Datepicker/DatepickerComponent/Atoms/datepickerAtom';
import {
    datepickerAtom,
    datepickerInputValueAtom
} from '@/components/base/Datepicker/DatepickerComponent/Atoms/datepickerAtom';
import Icon from '@/components/base/Icon';
import type { PopoverChildrenProps } from '@/components/base/Popover';
import Tooltip from '@/components/base/Tooltip';

type RecapDatepickerProps = Pick<PopoverChildrenProps, 'setReferenceElement'>;

export function RecapDatepicker({ setReferenceElement }: RecapDatepickerProps) {
    const { t } = useTranslation('layout_platform');
    const pathname = usePathname();
    const params = useParams();
    const datepickerData = useAtomValue(datepickerAtom as DatepickerRangeAtomImmer);

    const datepickerInputValue = useAtomValue(datepickerInputValueAtom);

    const isDisabled = getIsHeaderDatepickerGlobalDisabled({ pathname, params });

    return (
        <Tooltip disabled={!isDisabled} content={t('profiles.tooltip_disabled')}>
            <span>
                <PopoverButton
                    disabled={isDisabled}
                    className={clsx(
                        [
                            'flex !min-w-fit items-center gap-2 focus:ring-offset-0',
                            'h-8 px-2 py-2 sm:h-12 sm:px-4',
                            'disabled:opacity-40'
                        ],
                        {
                            [ButtonStyle['whiteNoBorder']]: isDisabled,
                            [ButtonStyle['whiteOpacity40']]: !isDisabled
                        }
                    )}
                    data-testid="recap-datepicker-global"
                    ref={setReferenceElement}>
                    <Icon icon="calendar" className="flex-shrink-0 text-icoBlue" />
                    <span className="flex min-w-0 flex-col text-left">
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-icoBlack dark:text-white">
                            {t(periodsTranslation[datepickerData.period])}
                        </span>
                        <span className="hidden font-GMedium text-xxs sm:inline">
                            {datepickerInputValue}
                        </span>
                    </span>
                    <Icon
                        icon="chevron-bottom"
                        className="flex-shrink-0 text-xs text-icoGray-400 dark:text-icoDarkMode-wolf"
                    />
                </PopoverButton>
            </span>
        </Tooltip>
    );
}
